import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { lazy, Suspense } from "react";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";

// Lazy load Tool Pages for code splitting
const NotesTool = lazy(() => import("./pages/tools/NotesTool"));
const TodoTool = lazy(() => import("./pages/tools/TodoTool"));
const PomodoroTool = lazy(() => import("./pages/tools/PomodoroTool"));
const QRGeneratorTool = lazy(() => import("./pages/tools/QRGeneratorTool"));
const ColorPickerTool = lazy(() => import("./pages/tools/ColorPickerTool"));
const UnitConverterTool = lazy(() => import("./pages/tools/UnitConverterTool"));
const PasswordGeneratorTool = lazy(() => import("./pages/tools/PasswordGeneratorTool"));
const TipCalculatorTool = lazy(() => import("./pages/tools/TipCalculatorTool"));
const LoanCalculatorTool = lazy(() => import("./pages/tools/LoanCalculatorTool"));
const CurrencyConverterTool = lazy(() => import("./pages/tools/CurrencyConverterTool"));
const RandomQuoteTool = lazy(() => import("./pages/tools/RandomQuoteTool"));
const DiceRollerTool = lazy(() => import("./pages/tools/DiceRollerTool"));
const CoinFlipTool = lazy(() => import("./pages/tools/CoinFlipTool"));

// Lazy load Static Pages
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const Contact = lazy(() => import("./pages/Contact"));

const queryClient = new QueryClient();

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-pulse text-muted-foreground">Loading...</div>
  </div>
);

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              
              {/* Productivity Tools */}
              <Route path="/tools/notes" element={<NotesTool />} />
              <Route path="/tools/todo" element={<TodoTool />} />
              <Route path="/tools/pomodoro" element={<PomodoroTool />} />
              
              {/* Utility Tools */}
              <Route path="/tools/qr-generator" element={<QRGeneratorTool />} />
              <Route path="/tools/color-picker" element={<ColorPickerTool />} />
              <Route path="/tools/unit-converter" element={<UnitConverterTool />} />
              <Route path="/tools/password-generator" element={<PasswordGeneratorTool />} />
              
              {/* Finance Tools */}
              <Route path="/tools/tip-calculator" element={<TipCalculatorTool />} />
              <Route path="/tools/loan-calculator" element={<LoanCalculatorTool />} />
              <Route path="/tools/currency-converter" element={<CurrencyConverterTool />} />
              
              {/* Entertainment Tools */}
              <Route path="/tools/random-quote" element={<RandomQuoteTool />} />
              <Route path="/tools/dice-roller" element={<DiceRollerTool />} />
              <Route path="/tools/coin-flip" element={<CoinFlipTool />} />
              
              {/* Static Pages */}
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
              <Route path="/about-us" element={<AboutUs />} />
              <Route path="/contact" element={<Contact />} />
              
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
